import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import parser from 'parse-address'
import moment from 'moment-timezone';

import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import { actions as kitActions } from '../pages/Kits/redux/actions';
import { actions as orgActions } from '../pages/Organizations/redux/actions';
import { DatePickerField, DateTimePickerField, Input, MultiSelectField, SelectField, TextArea } from '../containers/Form';
import { notificationActions } from '../components/Notification/redux/actions';
import { closeModal } from '../actions/modal';
import { CREATE_ORDER_RESULT, CREATE_SHIPMENT_ERROR, EDIT_ORDER_ERROR, EDIT_SHIPMENT_ERROR, GET_ORDER_RESULT, shippingActions } from '../pages/Shipping/actions';
import { ShipmentModalPages } from './_ModalsMetadata/ModalsMetadata';
import { shipmentToDTO, validateShipment, shipmentStatusEnum as shipmentStatus } from './_ModalsMetadata/Fields/shipmentFields';
import { getTooltipForNextButton } from './_ModalsMetadata/helpers';
import { shipmentStatusEnum } from '../utils/shipmentConstants';
import { SelectedOptions } from '../components/Select/Multiselect';
import { ReturnMethodEnum, OrderStatusEnum } from '../pages/Shipping/ShippingOrders';
import AddressFields from '../components/AddressFields/AddressFields';

export const ShippingMethods = [ "USPS Ground Advantage", "USPS Priority Mail", "UPS Ground", "UPS 2nd Day", "UPS 3rd Day", "UPS Next Day" ];
export const defaultShippingMethod = ShippingMethods[2];
const NOTE_CHAR_LIMIT = 4000;

export const formatDevices = devices => {
  const list = devices?.map(d => `${d.device_type} ${d.device_manufacturer} ${d.device_model} (${d.quantity})`);
  return (
    <div>
      <div className="label">{Strings.devices}:</div>
      {list?.length > 0 ? list.map((d, i) => <div key={i}>{d}</div>) : '-'}
    </div>
  );
};

function EditShipmentModal(props) {
  const { 
    data,
    editShipment,
    createShipment,
    createOrder,
    updateOrder,
    getKits,
    getOrderDetails,
    getOrgConfig,
    showNotification,
    closeModalWithNextAction,
  } = props;
  const { shipment: originalShipment, mode } = data;
  const initialKitIds = originalShipment?.kit_ids?.length
    ? originalShipment.kit_ids
    : originalShipment?.patient_kit_id
    ? [originalShipment.patient_kit_id]
    : [];

  // While a live SmartMeter order exists, the vendor's webhooks own the
  // shipment's transitions and dates; the backend rejects manual changes.
  const isSmartMeterManaged = originalShipment?.fulfillment_provider === 'smartmeter'
    && !['cancelled', 'error'].includes(originalShipment?.smartmeter_status);
  const recordedShipDate = originalShipment?.status_history?.find(s => s.status === shipmentStatus.in_transit)?.created_at;
  // ship_date acts only on the in_transit transition (ship/return modes) or as
  // a correction of an in_transit shipment's recorded date (edit mode); date
  // fields outside their mode stay out of the state so they are never sent.
  const initialShipDate = isSmartMeterManaged
    ? undefined
    : mode === 'ship' || mode === 'return'
    ? moment()
    : mode === 'edit' && originalShipment?.status === shipmentStatus.in_transit && recordedShipDate
    ? moment(recordedShipDate)
    : undefined;

  const [shipment, setShipment] = React.useState({
    ...originalShipment,
    eta: originalShipment?.eta ? moment(originalShipment?.eta) : null,
    delivered_date: mode !== 'delivered'
      ? undefined
      : originalShipment?.status_history?.find(s => s.status === shipmentStatus.delivered)?.created_at
      ? moment(originalShipment.status_history.find(s => s.status === shipmentStatus.delivered)?.created_at)
      : moment(),
    ship_date: initialShipDate,
    shipping_method: originalShipment?.shipping_method || defaultShippingMethod,
    kit_ids: initialKitIds,
    order_uuid: originalShipment?.order_uuid,
    return_target: originalShipment?.return_target || originalShipment?.suggested_return_targets?.find(t => t.is_recommended)?.org_uuid || '',
  });
  const [orderDetails, setOrderDetails] = React.useState();
  const [kits, setKits] = React.useState([]);
  const [note, setNote] = React.useState('');
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [saveBtnClicked, setSaveBtnClicked] = React.useState(false);

  const fields = ShipmentModalPages.kit.fields;
  const isDeliveredMode = mode === 'delivered';
  const targetState = mode === 'edit'
    ? shipment.status
    : isDeliveredMode
    ? shipmentStatus.delivered
    : mode === 'ship'
    ? shipmentStatus.in_transit
    : mode === 'return'
    ? shipmentStatus.in_transit
    : shipment.status;

  const shippedDate = React.useMemo(() => {
    const shippedStatus = originalShipment?.status_history?.find(s => s.status === shipmentStatus.in_transit)?.created_at;
    return shippedStatus ? moment(shippedStatus) : undefined;
  }, [originalShipment]);

  React.useEffect(() => {
    if (isDeliveredMode || mode === 'return') {
      return;
    }
    let cancelled = false;
    const loadEligibleKits = async () => {
      const eligibleKits = [];
      let offset = 0;
      let totalRecords;
      do {
        const resp = await getKits({
          eligibleForOrder: shipment.order_uuid,
          excludeShipment: shipment.shipment_uuid,
          limit: 100,
          offset,
        });
        const page = resp?.response?.kits || [];
        eligibleKits.push(...page);
        totalRecords = resp?.response?.pagination?.totalRecords ?? eligibleKits.length;
        offset += page.length;
        if (page.length === 0) break;
      } while (offset < totalRecords);

      if (!cancelled) {
        setKits(eligibleKits.map(d => ({
          label: `${d.id}${d.name ? ` (${d.name})` : ''}`,
          value: d.id,
          name: d.name,
        })));
      }
    };
    loadEligibleKits();
    return () => { cancelled = true; };
  }, [getKits, isDeliveredMode, mode, shipment.order_uuid, shipment.shipment_uuid]);

  React.useEffect(() => {
    if (!shipment?.order_uuid) {
      return;
    }
    getOrderDetails(shipment.order_uuid).then(resp => {
      if (resp.type === GET_ORDER_RESULT) {
        setOrderDetails(resp.response);
        setNote(resp.response.notes || '');
        setShipment(s => ({
          ...s,
          shipping_address: {
            street: s.shipping_address?.street || resp.response.shipping_address?.street || '',
            street2: s.shipping_address?.street2 || resp.response.shipping_address?.street2 || '',
            city: s.shipping_address?.city || resp.response.shipping_address?.city || '',
            state: s.shipping_address?.state || resp.response.shipping_address?.state || '',
            zip: s.shipping_address?.zip || resp.response.shipping_address?.zip || '',
            country: s.shipping_address?.country || resp.response.shipping_address?.country || '',
          },
          contact_details: {
            recipient_name: s.contact_details?.recipient_name || resp.response.contact_details?.recipient_name || '',
            phone: s.contact_details?.phone || resp.response.contact_details?.phone || '',
            email: s.contact_details?.email || resp.response.contact_details?.email || '',
          },
          provider_order_no: s.provider_order_no || resp.response.provider_order_no || '',
        }));
      }
    });
  }, [shipment.order_uuid]);

  React.useEffect(() => {
    const errors = validateShipment(shipment, targetState, originalShipment?.flow !== 'return');
    if (note && note.length > NOTE_CHAR_LIMIT) {
      errors.push({ property: 'note', propertyName: Strings.note, errors: [Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, NOTE_CHAR_LIMIT)], missing: false });
    }
    if (mode === 'return' && !shipment.return_target) {
      errors.push({ property: 'return_target', propertyName: Strings.shipping.warehouseToShipDevices, errors: [Strings.shipping.returnTargetRequired], missing: true });
    }

    setValidationErrors(errors);
  }, [shipment, note]);

  React.useEffect(() => {
    if (shipment?.return_target) {
      getOrgConfig(shipment.return_target).then(resp => {
        let parsedAddress = {};
        if (resp?.response?.config?.contact_details?.address) {
          parsedAddress = parser.parseLocation(resp.response.config.contact_details.address);
        }
        setShipment(s => ({
          ...s,
          shipping_address: {
            street: `${parsedAddress?.number ? `${parsedAddress?.number} ` : ''}${parsedAddress?.street || ''}${parsedAddress?.type ? ` ${parsedAddress?.type}` : ''}`,
            street2: parsedAddress?.sec_unit_type ? `${parsedAddress?.sec_unit_type}${parsedAddress?.sec_unit_num ? ` ${parsedAddress?.sec_unit_num}` : ''}` : '',
            city: parsedAddress?.city,
            state: parsedAddress?.state,
            zip: `${parsedAddress?.plus4 ? `${parsedAddress?.plus4}-` : ''}${parsedAddress?.zip || ''}`,
            country: "USA",
          },
          contact_details: {
            recipient_name: resp?.response?.config?.contact_details?.primary_contact?.name || '',
            phone: resp?.response?.config?.contact_details?.primary_contact?.phone || '',
            email: resp?.response?.config?.contact_details?.primary_contact?.email || '',
          },
        }));
      });
    }
  }, [shipment?.return_target]);

  const saveNote = async () => {
    if (note) {
      await updateOrder(shipment?.order_uuid, { notes: note }).then(resp => {
        if (resp.type === EDIT_ORDER_ERROR) {
          const error = resp.response?.data?.error?.message;
          showNotification(error, 5000, true);
          return false;
        }
      });
      return true;
    }
    return true;
  };

  const createOrderForReturnShipment = async () => {
    const orderResp = await createOrder({
      flow: 'return',
      order_type: 'standard',
      order_category: shipment?.return_method === ReturnMethodEnum.mail ? 'direct' : 'bulk',
      patient_uuid: shipment.patient_uuid,
      enrollment_uuids: shipment.enrollment_uuids,
      source_kit_uuid: shipment?.return_method === ReturnMethodEnum.mail ? shipment.kit_ids?.[0] : undefined,
      target_org_uuid: shipment.return_target,
      shipping_address: shipment.shipping_address,
      notes: note || undefined,
    })
    const order = orderResp?.response;
    if (orderResp?.type !== CREATE_ORDER_RESULT) {
      const error = orderResp.response?.data?.error?.message;
      showNotification(error, 5000, true);
      return false;
    }
    return order;
  };

  const onSubmit = async () => {
    if (saveBtnClicked) {
      return;
    }
    setSaveBtnClicked(true);

    let fail = false;
    let order;
    let createdShipmentUuid;

    const isNewReturnShipment = mode === 'return' && !shipment.order_uuid;
    if (isNewReturnShipment) {
      order = await createOrderForReturnShipment();
      if (!order) {
        fail = true;
      }
    }

    if (!fail) {
      const shipmentData = shipmentToDTO(
        {... shipment, order_uuid: shipment.order_uuid || order?.order_uuid },
        { mode, status: originalShipment?.status, recordedShipDate, originalEta: originalShipment?.eta, isSmartMeterManaged },
      );
      if (mode === 'delivered') {
        shipmentData.status = shipmentStatus.delivered;
      } else if (mode !== 'edit' && mode !== 'return') {
        shipmentData.status = 'in_transit';
      }

      let response;
      if (shipment.shipment_uuid) {
        response = await editShipment(shipment.shipment_uuid, shipmentData);
      } else {
        const responseCreate = await createShipment(shipmentData);
        if (responseCreate.type === CREATE_SHIPMENT_ERROR) {
          showNotification(responseCreate.response?.data?.error?.message, 5000, true);
          fail = true;
        } else {
          createdShipmentUuid = responseCreate.response.shipment_uuid;
          response = await editShipment(
            createdShipmentUuid,
            {...shipmentData, status: mode === 'return' ? shipmentStatusEnum.in_transit : shipmentStatusEnum.created}
          );
        }
      }
      if (response) {
        if (response.type === EDIT_SHIPMENT_ERROR) {
          const error = response.response?.data?.standard_error?.message;
          showNotification(error, 5000, true);
          fail = true;
        } 
      }
    }

    // A return order and its shipment are created in two separate calls, so a
    // failure after the order was created leaves the order behind. If no
    // shipment row was created (e.g. the kit is still on an active outbound
    // shipment), roll the order back — otherwise it lingers as an orphaned
    // active return order and a second Save trips the one-active-return-per-kit
    // guard, surfacing a different, confusing error (RXC-1349).
    if (fail && isNewReturnShipment && order?.order_uuid && !createdShipmentUuid) {
      await updateOrder(order.order_uuid, { status: OrderStatusEnum.cancelled });
    }

    if (!fail && !isNewReturnShipment) {
      fail = !await saveNote();
    }

    if (!fail) {
      showNotification(Strings.shipping.shipmentCreated);
      closeModalWithNextAction();
    }

    setSaveBtnClicked(false);
  };

  const onDataChange = (name, value) => {
    let v = value;
    if (name === 'shipping_cost') {
      const val = parseFloat(value);
      if (!isNaN(val)) {
        v = val;
      } else {
        v = '';
      }
    }
    setShipment(s => ({
      ...s,
      [name]: v,
    }));
  };

  const onAddressChange = (name, value) => {
    setShipment(s => ({
      ...s,
      shipping_address: {
        ...s.shipping_address,
        [name]: value,
      },
    }));
  };

  const requiredFields = React.useMemo(() => {
    return Object.values(fields).filter(f => f.required(shipment, targetState, shipment?.flow !== 'return')).map(f => f.fieldId);
  }, [shipment]);

  const knownKitOptions = [...(originalShipment?.kit_ids || []), ...(originalShipment?.patient_kit_id ? [originalShipment.patient_kit_id] : [])]
    .filter((kitId, index, kitIds) => kitIds.indexOf(kitId) === index)
    .map(kitId => ({ label: kitId, value: kitId }));
  const availableKitOptions = [
    ...knownKitOptions,
    ...kits.filter(kit => !knownKitOptions.some(knownKit => knownKit.value === kit.value)),
  ];
  const kitsOptions =
    // Existing direct and return shipments keep their single physical kit.
    ((originalShipment?.order_category === 'direct' || mode === 'return') && originalShipment?.kit_ids?.length === 1)
    ? [{ label: originalShipment.kit_ids?.[0], value: originalShipment.kit_ids?.[0] }]
    : availableKitOptions;

  const noteTextArea = React.useMemo(() => (
    <TextArea
      name="note"
      id="note"
      rows={4}
      value={note}
      label={Strings.shipping.orderNotes}
      onChange={e => setNote(e.target.value)}
      errorsForTooltip={validationErrors.find(v => v.property === 'note')?.errors || []}
      highlightInvalid={highlightInvalidFields}
    />
  ), [note, validationErrors, highlightInvalidFields]);

  const kitField = React.useMemo(() => {
    if (mode === 'return') {
      return (
        <div style={{ color: '#4F4F4F' }}>
          {Strings.shipping.kitsToReturn}:
          {shipment?.kit_ids?.map((kitId, i) => (
            <div key={i}>- {kitId}</div>
          ))}
        </div>
      );
    }
    if (orderDetails?.order_category === 'direct') {
      return (
        <SelectField
          id="kit"
          name="kit"
          label={Strings.kitId}
          placeholder={Strings.scanBarcodeOrEnterId}
          value={shipment?.kit_ids?.[0] || ''}
          data={kitsOptions}
          onChange={o => onDataChange('kit_ids', o.value ? [o.value] : [] )}
          isSearchable
          isRequired={requiredFields.includes('kit_ids')}
          highlightInvalid={highlightInvalidFields}
          rowClassName="double-width-left"
        />
      )
    }
    return (
      <>
        <MultiSelectField
          id="kit"
          name="kit"
          label={Strings.kitId}
          placeholder={Strings.scanBarcodeOrEnterId}
          value={shipment?.kit_ids || []}
          data={kitsOptions}
          onChange={o => onDataChange('kit_ids', o?.map(v => v.value) || [] )}
          isSearchable
          checkboxFirst
          isRequired={requiredFields.includes('kit_ids')}
          highlightInvalid={highlightInvalidFields}
          rowClassName="double-width-left"
        />
        <SelectedOptions
          items={kitsOptions.filter(k => shipment?.kit_ids?.includes(k.value))}
          onRemove={o => onDataChange('kit_ids', shipment?.kit_ids?.filter(id => id !== o.value) || [])}
          itemsPerRow={1}
        />
      </>
    )
  }, [shipment?.kit_ids, kitsOptions, requiredFields, highlightInvalidFields]);

  const pages = [
    {
      id: ShipmentModalPages.kit.id,
      title: isDeliveredMode 
        ? Strings.shipping.markAsDelivered 
        : mode === 'ship' 
        ? Strings.shipping.sendPackage 
        : shipment.shipment_uuid 
        ? Strings.shipping.editShipment 
        : ShipmentModalPages.kit.name,
      content: isDeliveredMode ? (
        <>
          {!isSmartMeterManaged && (
            <DateTimePickerField
              label={Strings.shipping.deliveryDate}
              name="delivered_date"
              id="delivered_date"
              onChange={e => onDataChange('delivered_date', e)}
              value={shipment.delivered_date}
              maxDate={moment()}
              minDate={shippedDate}
              isRequired
              errorsForTooltip={validationErrors.find(v => v.property === 'delivered_date')?.errors || []}
              highlightInvalid={highlightInvalidFields}
            />
          )}
          {noteTextArea}
        </>
      ) : (
        <>
          {orderDetails?.order_items?.length > 0 && formatDevices(orderDetails?.order_items)}
          {shipment?.contact_details && (
            <div>
              <div className="label">{Strings.shipping.contactDetails}:</div>
              {[shipment.contact_details.recipient_name, shipment.contact_details.phone, shipment.contact_details.email].map((detail, i) => {
                if (detail) {
                  return <div key={i}>{detail}</div>;
                }
                return null;
              })}
            </div>
          )}
          {mode === 'return' && shipment?.suggested_return_targets && (
            <SelectField
              label={Strings.shipping.warehouseToShipDevices}
              name="return_target"
              id="return_target"
              placeholder={Strings.select}
              data={shipment.suggested_return_targets.map(t => ({ label: t.name, value: t.org_uuid }))}
              onChange={option => onDataChange('return_target', option.value)}
              value={shipment.return_target}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
          )}
          <AddressFields
            address={shipment.shipping_address}
            onAddressChange={onAddressChange}
            isRequired={requiredFields.includes('shipping_address')}
            highlightInvalid={highlightInvalidFields}
            groupedRows
          />
          {kitField}
          <SelectField
            label={Strings.shipping.shippingMethod}
            type="text"
            name="shipping_method"
            id="shipping_method"
            placeholder={Strings.select}
            data={ShippingMethods.map(m => ({ label: m, value: m }))}
            onChange={option => onDataChange('shipping_method', option.value)}
            value={shipment.shipping_method}
            isRequired={requiredFields.includes('shipping_method')}
            highlightInvalid={highlightInvalidFields}
          />
          <Input
            label={Strings.shipping.trackingNumber}
            type="text"
            name="tracking_number"
            id="tracking_number"
            onChange={e => onDataChange('tracking_number', e.target.value)}
            value={shipment.tracking_number}
            isRequired={requiredFields.includes('tracking_number')}
            highlightInvalid={highlightInvalidFields}
          />
          {mode !== 'return' && (
            <>
              <Input
                label={Strings.shipping.providerOrderNumber}
                type="text"
                name="provider_order_no"
                id="provider_order_no"
                onChange={e => onDataChange('provider_order_no', e.target.value)}
                value={shipment.provider_order_no}
                isRequired={requiredFields.includes('provider_order_no')}
                highlightInvalid={highlightInvalidFields}
              />
              <Input
                label={Strings.shipping.salesOrderNumber}
                type="text"
                name="sales_order_no"
                id="sales_order_no"
                onChange={e => onDataChange('sales_order_no', e.target.value)}
                value={shipment.sales_order_no}
                isRequired={requiredFields.includes('sales_order_no')}
                highlightInvalid={highlightInvalidFields}
              />
              <Input
                label={Strings.shipping.shippingCost}
                type="number"
                name="shipping_cost"
                id="shipping_cost"
                onChange={e => onDataChange('shipping_cost', e.target.value)}
                value={shipment.shipping_cost}
                isRequired={requiredFields.includes('shipping_cost')}
                highlightInvalid={highlightInvalidFields}
              />
            </>
          )}
          {!isSmartMeterManaged && (mode === 'ship' || mode === 'return' || (mode === 'edit' && originalShipment?.status === shipmentStatus.in_transit)) && (
            <DatePickerField
              label={Strings.shipping.shippingDate}
              name="ship_date"
              id="ship_date"
              onChange={e => onDataChange('ship_date', e)}
              value={shipment.ship_date}
              maxDate={moment()}
              errorsForTooltip={validationErrors.find(v => v.property === 'ship_date')?.errors || []}
              highlightInvalid={highlightInvalidFields}
            />
          )}
          {!isSmartMeterManaged && (
            <DatePickerField
              label={Strings.shipping.eta}
              name="eta"
              id="eta"
              onChange={e => onDataChange('eta', e)}
              value={shipment.eta}
              isRequired={requiredFields.includes('eta')}
              errorsForTooltip={validationErrors.find(v => v.property === 'eta')?.errors || []}
              highlightInvalid={highlightInvalidFields}
            />
          )}
          {noteTextArea}
        </>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !saveBtnClicked,
    },
  ];

  return (
    <Wizard
      name="edit-shipment"
      pages={pages}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      onSubmit={onSubmit}
      showPagesFilter={false}
      nextButtonTooltip={getTooltipForNextButton(validationErrors)}
    />
  );
}

EditShipmentModal.propTypes = {
  data: PropTypes.shape({
    vitals: PropTypes.array,
    shipment: PropTypes.object,
    onSuccess: PropTypes.func,
    mode: PropTypes.string,
  }),
  createShipment: PropTypes.func.isRequired,
  createOrder: PropTypes.func.isRequired,
  editShipment: PropTypes.func.isRequired,
  updateOrder: PropTypes.func.isRequired,
  getOrderDetails: PropTypes.func.isRequired,
  getOrgConfig: PropTypes.func.isRequired,
  getKits: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  closeModalWithNextAction: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  editShipment: (id, data) => dispatch(shippingActions.editShipment(id, data)),
  createShipment: data => dispatch(shippingActions.createShipment(data)),
  createOrder: (orderData) => dispatch(shippingActions.createOrder(orderData)),
  getOrderDetails: orderId => dispatch(shippingActions.getOrder(orderId)),
  getKits: pageRequest => dispatch(kitActions.getKits(pageRequest)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  updateOrder: (id, orderData) => dispatch(shippingActions.editOrder(id, orderData)),
  onCancel: () => dispatch(closeModal(ownProps.name)),
  getOrgConfig: orgId => dispatch(orgActions.getOrgConfig({organizationId: orgId})),
  closeModalWithNextAction: () => {
    dispatch(closeModal('edit-shipment'));
    if (ownProps.data.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(EditShipmentModal);
